import { readPage, unwrapItem, type Page, type SessionClient } from '@portal/platform/http';
import type {
  ComplianceActionType,
  ComplianceReportQuery,
  ComplianceReportRow,
  ResolvedServiceComplianceConfig,
  ServiceComplianceConfigInput,
} from './types';

/**
 * The monitor's endpoints, ported from `ServiceAgreementComplianceMonitorService`. The org
 * scope rides on the session client (`?orgNodeId=`); nothing here adds it.
 */

/** The report endpoint's `limit` maximum (`complianceReportQuerySchema`). */
export const REPORT_MAX_LIMIT = 200;

export async function fetchComplianceReport(
  client: SessionClient,
  query: ComplianceReportQuery,
): Promise<Page<ComplianceReportRow>> {
  const { body, headers } = await client.withHeaders('/service-agreement-compliance/report', {
    query: {
      tab: query.tab,
      search: query.search.trim() || undefined,
      onlyOver: query.onlyOver ? 'true' : undefined,
      sortBy: query.sortBy,
      sortOrder: query.sortBy ? query.sortOrder : undefined,
      page: query.page,
      limit: query.limit,
    },
  });
  return readPage<ComplianceReportRow>(body, headers);
}

/**
 * Every row of the current filter, for the CSV export, a page of {@link REPORT_MAX_LIMIT} at a
 * time. Angular asked for one page of 1000, which the endpoint refuses (400) — so its export
 * never worked.
 */
export async function fetchComplianceReportForExport(
  client: SessionClient,
  query: Omit<ComplianceReportQuery, 'page' | 'limit'>,
): Promise<ComplianceReportRow[]> {
  const rows: ComplianceReportRow[] = [];
  for (let page = 1; ; page += 1) {
    const result = await fetchComplianceReport(client, {
      ...query,
      page,
      limit: REPORT_MAX_LIMIT,
    });
    rows.push(...result.items);
    const done =
      result.items.length < REPORT_MAX_LIMIT ||
      (result.total !== null ? rows.length >= result.total : !result.hasNext);
    if (done) {
      return rows;
    }
  }
}

/** Record a dealer action. `initials` is required: the dealership shares one login. */
export async function takeComplianceAction(
  client: SessionClient,
  input: {
    serviceAgreementId: string;
    type: ComplianceActionType;
    initials: string;
    note?: string;
  },
): Promise<void> {
  await client.json(
    `/service-agreement-compliance/${encodeURIComponent(input.serviceAgreementId)}/actions`,
    {
      method: 'POST',
      json: {
        type: input.type,
        initials: input.initials,
        ...(input.note ? { note: input.note } : {}),
      },
    },
  );
}

/** The thresholds the classifier uses at the selected node, each with where it came from. */
export async function fetchComplianceConfig(
  client: SessionClient,
): Promise<ResolvedServiceComplianceConfig> {
  return unwrapItem<ResolvedServiceComplianceConfig>(
    await client.json('/service-compliance-configs/current'),
  );
}

/** Write the selected node's override. Answers the stored row, not the resolved config. */
export async function saveComplianceConfig(
  client: SessionClient,
  input: ServiceComplianceConfigInput,
): Promise<void> {
  await client.json('/service-compliance-configs/current', { method: 'PUT', json: input });
}
